import "../css/visual-editor.scss";

import {
  arrayHasLength,
  getPageZoneSelector,
  getZoneShortCodeAndZonePageCodeId,
  getURLSearchParams,
  isObject,
  useIsomorphicRequestAnimationLayout,
  waitForElement,
  getImageZoneElement,
  addBodyDataAttribute,
} from "../shared";
import { ContentTypeName } from "../types";
import {
  VisualEditorConstants,
  VisualEditorMessageEvent,
  VisualEditorMessageEventDataType,
  VisualEditorState,
} from "../types/visual-editor.types";

export const VisualEditorModule = (() => {
  const state: VisualEditorState = {
    observer: null,
    previewZoneOverlayElement: null,
    previewZoneTagsByZoneShortCodeAndZonePageCodeId: {},
    defaultZoneContentZoneShortCodeAndZonePageCodeId: {},
    verifiedZonesByZoneId: {},
    isEditMode: false,
  };

  const handlePreviewTabClick = (zoneName: string) => (event: MouseEvent) => {
    if (!event.target) return;

    event.stopImmediatePropagation();
    event.stopPropagation();
    event.preventDefault();

    const targetElement = event.target as HTMLElement;

    if (targetElement.getAttribute("data-visual-editor-clickable") === "true") {
      parent.postMessage(
        {
          type: VisualEditorMessageEventDataType.PreviewTabClicked,
          context: VisualEditorConstants.BattingCageScript,
          zoneName,
        },
        "*"
      );
    }
  };

  const restoreDefaultZoneContent = ({
    contentTypeName,
    element,
    pageShortCode,
    zoneName,
    zoneShortCode,
  }: {
    contentTypeName: string;
    element: HTMLElement;
    pageShortCode: string;
    zoneName: string;
    zoneShortCode: string;
  }) => {
    const zoneShortCodeAndZonePageCodeId = getZoneShortCodeAndZonePageCodeId({
      pageShortCode,
      zoneShortCode,
    });

    const hasDefaultZoneContent =
      zoneShortCodeAndZonePageCodeId in
      state.defaultZoneContentZoneShortCodeAndZonePageCodeId;

    if (hasDefaultZoneContent) {
      const defaultZoneContent =
        state.defaultZoneContentZoneShortCodeAndZonePageCodeId[
          zoneShortCodeAndZonePageCodeId
        ];
      const previewDivElement =
        state.previewZoneTagsByZoneShortCodeAndZonePageCodeId[
          zoneShortCodeAndZonePageCodeId
        ];

      if (contentTypeName === ContentTypeName.Text) {
        const htmlContent = defaultZoneContent.text;

        if (htmlContent) {
          element.innerHTML = htmlContent;
          previewDivElement && element.prepend(previewDivElement);
        }

        if (!previewDivElement) {
          createPreviewZoneTag({
            contentTypeName,
            element,
            zoneName,
            zoneShortCodeAndZonePageCodeId,
          });
        }
      }

      if (contentTypeName === ContentTypeName.Image) {
        const imageElement = getImageZoneElement({ element });

        if (imageElement) {
          const imageAlt = defaultZoneContent.assetAltText || "";
          const imageSource = defaultZoneContent.assetUrl;

          if (imageSource) {
            updateImageElement(imageElement, imageSource, imageAlt);
          }
        }
      }
    }
  };

  const hasRequiredAttributes = (
    element: Element,
    contentTypeName: keyof typeof ContentTypeName
  ): boolean => {
    if (contentTypeName === ContentTypeName.Text) {
      return Boolean(
        element.hasAttribute("data-visual-editor-preview-zone") &&
          element.querySelector(".preview-tab")
      );
    }
    return Boolean(
      element.hasAttribute("data-visual-editor-preview-zone-image") &&
        element.querySelector(".preview-tab .preview-tab--img")
    );
  };

  const updateImageElement = (
    imageElement: HTMLImageElement,
    imageSource: string,
    imageAlt: string
  ) => {
    imageElement.src = imageSource;
    imageElement.alt = imageAlt;
    imageElement.setAttribute("data-source", imageSource);
    imageElement.setAttribute("data-uw-rm-ima-original", imageAlt);
  };

  const createPreviewElement = (
    zoneName: string,
    isImage: boolean = false
  ): HTMLSpanElement => {
    const previewSpanElement = document.createElement("span");
    const classes = ["preview-tab"];
    if (isImage) classes.push("preview-tab--img");

    previewSpanElement.classList.add(...classes);
    previewSpanElement.append(zoneName);
    previewSpanElement.setAttribute("data-zone-name", zoneName);
    previewSpanElement.setAttribute("data-visual-editor-clickable", "true");
    previewSpanElement.setAttribute(
      isImage
        ? "visual-editor-preview-zone-image"
        : "visual-editor-preview-tab",
      "true"
    );

    previewSpanElement.addEventListener(
      "click",
      handlePreviewTabClick(zoneName)
    );

    return previewSpanElement;
  };

  const createPreviewZoneTag = ({
    contentTypeName,
    element,
    zoneName,
    zoneShortCodeAndZonePageCodeId,
  }: {
    contentTypeName: keyof typeof ContentTypeName;
    element: Element;
    zoneName: string;
    zoneShortCodeAndZonePageCodeId: string;
  }) => {
    if (!hasRequiredAttributes(element, contentTypeName)) {
      const attributeName =
        contentTypeName === ContentTypeName.Text
          ? "data-visual-editor-preview-zone"
          : "data-visual-editor-preview-zone-image";

      element.setAttribute(attributeName, "");

      const previewDivElement = createPreviewElement(
        zoneName,
        contentTypeName === ContentTypeName.Image
      );

      element.prepend(previewDivElement);

      state.previewZoneTagsByZoneShortCodeAndZonePageCodeId[
        zoneShortCodeAndZonePageCodeId
      ] = previewDivElement;
    }
  };

  const toggleHideZoneAttribute = (
    element: HTMLElement,
    shouldHideZone: boolean
  ) => {
    element.setAttribute("data-bc-hidden", shouldHideZone ? "true" : "false");
  };

  const setupVisualEditorMode = async () => {
    parent.postMessage(
      {
        type: VisualEditorMessageEventDataType.Init,
        init: true,
        context: VisualEditorConstants.BattingCageScript,
      },
      "*"
    );

    window.addEventListener(
      "message",
      function (event: MessageEvent<VisualEditorMessageEvent>) {
        if (
          !event.origin.includes("dugout") &&
          !event.origin.includes("http://localhost:6075")
        ) {
          return;
        }

        let eventData = {} as VisualEditorMessageEvent;

        try {
          const parsedEvent =
            typeof event.data === "string"
              ? JSON.parse(event.data)
              : event.data;

          if (isObject(parsedEvent)) eventData = parsedEvent;
        } catch (e) {}

        if (eventData?.context !== VisualEditorConstants.BattingCageScript)
          return;

        const {
          isEditMode: isEditModeEventData,
          pageZones,
          type,
          zone,
        } = eventData;

        const {
          content,
          contentType,
          pageShortCode = "",
          zoneShortCode = "",
          zoneName = "",
          zoneId,
        } = zone || {};

        const eventZoneSelector = getPageZoneSelector({
          pageShortCode,
          zoneShortCode,
        });

        switch (type) {
          case VisualEditorMessageEventDataType.EditZoneContent: {
            if (!zone && !eventZoneSelector) return;

            const callBack = (element?: HTMLElement) => {
              if (
                element &&
                content &&
                contentType &&
                zoneShortCode &&
                zoneName
              ) {
                const contentTypeName = contentType.name;

                const zoneShortCodeAndZonePageCodeId =
                  getZoneShortCodeAndZonePageCodeId({
                    pageShortCode,
                    zoneShortCode,
                  });

                const shouldStoreDefaultZoneContent = !Boolean(
                  state.defaultZoneContentZoneShortCodeAndZonePageCodeId[
                    zoneShortCodeAndZonePageCodeId
                  ]
                );

                if (contentTypeName === ContentTypeName.Text) {
                  if (shouldStoreDefaultZoneContent) {
                    state.defaultZoneContentZoneShortCodeAndZonePageCodeId[
                      zoneShortCodeAndZonePageCodeId
                    ] = {
                      text: element.innerHTML,
                    };
                  }

                  const previewDivElement =
                    state.previewZoneTagsByZoneShortCodeAndZonePageCodeId[
                      zoneShortCodeAndZonePageCodeId
                    ];

                  const htmlContent = content.text;

                  if (htmlContent) {
                    element.innerHTML = htmlContent;
                    previewDivElement && element.prepend(previewDivElement);
                  } else {
                    if (state.isEditMode) {
                      // Todo - add cursor maybe
                      element.replaceChildren();

                      previewDivElement && element.prepend(previewDivElement);
                      const cursorElement = document.createElement("div");
                      cursorElement.classList.add("preview-cursor");
                      element.append(cursorElement);
                    } else {
                      restoreDefaultZoneContent({
                        contentTypeName: contentType.name,
                        element,
                        pageShortCode,
                        zoneName,
                        zoneShortCode,
                      });
                    }
                  }

                  if (!previewDivElement) {
                    createPreviewZoneTag({
                      contentTypeName,
                      element,
                      zoneName,
                      zoneShortCodeAndZonePageCodeId,
                    });
                  }
                }

                if (contentType.name === ContentTypeName.Image) {
                  const imageElement = getImageZoneElement({ element });

                  if (imageElement) {
                    const imageAlt = content.assetAltText || "";
                    const imageSource = content.assetUrl;

                    if (shouldStoreDefaultZoneContent) {
                      state.defaultZoneContentZoneShortCodeAndZonePageCodeId[
                        zoneShortCodeAndZonePageCodeId
                      ] = {
                        assetUrl: imageElement.src,
                        assetAltText: imageElement.alt,
                        ["data-source"]:
                          imageElement.getAttribute("data-source"),
                        ["data-uw-rm-ima-original"]: imageElement.getAttribute(
                          "data-uw-rm-ima-original"
                        ),
                      };
                    }

                    if (imageSource) {
                      updateImageElement(imageElement, imageSource, imageAlt);
                    } else {
                      //     /**
                      //      * If a user selects a new image for a zone,
                      //      * then deletes it, the zone should revert back
                      //      * to the original image.
                      //      *
                      //      * Otherwise, the deleted image on the zone would
                      //      * remain showing on the UI
                      //      */

                      const hasDefaultZoneContent =
                        zoneShortCodeAndZonePageCodeId in
                        state.defaultZoneContentZoneShortCodeAndZonePageCodeId;

                      if (hasDefaultZoneContent) {
                        const defaultZoneContent =
                          state
                            .defaultZoneContentZoneShortCodeAndZonePageCodeId[
                            zoneShortCodeAndZonePageCodeId
                          ];
                        if (!defaultZoneContent.assetUrl) return;

                        imageElement.src = defaultZoneContent.assetUrl;
                        imageElement.alt =
                          defaultZoneContent.assetAltText ?? "";
                        imageElement.setAttribute(
                          "data-source",
                          defaultZoneContent["data-source"] ?? ""
                        );
                        imageElement.setAttribute(
                          "data-uw-rm-ima-original",
                          defaultZoneContent["data-uw-rm-ima-original"] ?? ""
                        );
                      }
                    }
                  }
                }
              }
            };

            waitForElement(eventZoneSelector, callBack);
          }

          case VisualEditorMessageEventDataType.HideZone: {
            if (!zone && !eventZoneSelector) return;

            const shouldHideZone = Boolean(content?.hideZone);

            const callBack = (element?: HTMLElement) => {
              if (element) {
                toggleHideZoneAttribute(element, shouldHideZone);
              }
            };

            waitForElement(eventZoneSelector, callBack);

            break;
          }

          case VisualEditorMessageEventDataType.RestoreDefaultZoneContent: {
            if (!zone && !eventZoneSelector) return;

            const callBack = (element?: HTMLElement) => {
              if (element && contentType && zoneShortCode) {
                restoreDefaultZoneContent({
                  contentTypeName: contentType.name,
                  element,
                  pageShortCode,
                  zoneName,
                  zoneShortCode,
                });
              }
            };

            waitForElement(eventZoneSelector, callBack);

            break;
          }

          case VisualEditorMessageEventDataType.SetZoneEditMode: {
            state.isEditMode = isEditModeEventData;
            break;
          }

          case VisualEditorMessageEventDataType.ScrollToZone: {
            if (!zone && !eventZoneSelector) return;

            const callBack = (element?: HTMLElement) => {
              if (element) {
                useIsomorphicRequestAnimationLayout(() => {
                  element.scrollIntoView({
                    behavior: "instant",
                    block: "center",
                    inline: "center",
                  });
                });
              }
            };

            waitForElement(eventZoneSelector, callBack);

            break;
          }

          case VisualEditorMessageEventDataType.VerifyInitialZones:
            if (arrayHasLength(pageZones)) {
              const totalAmountOfZones = pageZones.length;

              pageZones.forEach(async (pageZone) => {
                const {
                  zoneShortCode,
                  pageShortCode,
                  zoneName,
                  content,
                  contentType,
                  zoneId,
                } = pageZone;

                const pageZoneSelector = getPageZoneSelector({
                  pageShortCode,
                  zoneShortCode,
                });

                const zoneShortCodeAndZonePageCodeId =
                  getZoneShortCodeAndZonePageCodeId({
                    pageShortCode,
                    zoneShortCode,
                  });

                const callBack = (element?: HTMLElement) => {
                  if (element) {
                    const contentTypeName = contentType.name;

                    const shouldHideZone = Boolean(content?.hideZone);

                    if (contentTypeName === ContentTypeName.Text) {
                      state.defaultZoneContentZoneShortCodeAndZonePageCodeId[
                        zoneShortCodeAndZonePageCodeId
                      ] = {
                        text: element.innerHTML,
                      };

                      const htmlContent = content?.text;

                      if (htmlContent) {
                        element.replaceChildren();

                        element.insertAdjacentHTML("beforeend", htmlContent);
                      }
                    }

                    if (contentTypeName === ContentTypeName.Image) {
                      const imageElement = getImageZoneElement({ element });

                      if (imageElement && content?.assetUrl) {
                        state.defaultZoneContentZoneShortCodeAndZonePageCodeId[
                          zoneShortCodeAndZonePageCodeId
                        ] = {
                          assetUrl: imageElement.src,
                          assetAltText: imageElement.alt,
                          ["data-source"]:
                            imageElement.getAttribute("data-source"),
                          ["data-uw-rm-ima-original"]:
                            imageElement.getAttribute(
                              "data-uw-rm-ima-original"
                            ),
                        };

                        const imageAlt = content.assetAltText || "";
                        const imageSource = content.assetUrl;

                        updateImageElement(imageElement, imageSource, imageAlt);
                      }
                    }

                    createPreviewZoneTag({
                      contentTypeName,
                      element,
                      zoneName,
                      zoneShortCodeAndZonePageCodeId,
                    });

                    if (shouldHideZone) {
                      toggleHideZoneAttribute(element, shouldHideZone);
                    }

                    state.verifiedZonesByZoneId[zoneId] = true;
                  }
                };

                await waitForElement(pageZoneSelector, callBack);
              });
            }
            break;

          default:
            break;
        }
      }
    );
  };

  const start = () => {
    const isVisualEditorMode =
      getURLSearchParams("preview_visual_editor")?.[0] === "true";

    if (isVisualEditorMode) {
      addBodyDataAttribute("visual-editor-mode", "true");
      setupVisualEditorMode();
    }
    return {
      isVisualEditorMode,
    };
  };

  return {
    start,
  };
})();
